import { enhancePreparedOcrImage, type OcrTextImageEnhancement, type PreparedOcrImage } from '@/core/image'
import type { PixelRect } from '@/domain/types'

type Color = readonly [number, number, number]

function backgroundColor(image: ImageData, area: PixelRect): Color | null {
  const bins = new Map<number, { count: number; rgb: number[] }>()
  for (let y = area.y; y < Math.min(image.height, area.y + area.height); y++) {
    for (let x = area.x; x < Math.min(image.width, area.x + area.width, area.x + area.height * 2); x++) {
      const i = (y * image.width + x) * 4, r = image.data[i]!, g = image.data[i + 1]!, b = image.data[i + 2]!
      if (r < 65 || b < r + 8 || g < r + 6 || b >= 245) continue
      const key = (r >> 3) * 1024 + (g >> 3) * 32 + (b >> 3)
      const bin = bins.get(key) ?? { count: 0, rgb: [0, 0, 0] }
      bin.count++; bin.rgb[0]! += r; bin.rgb[1]! += g; bin.rgb[2]! += b
      bins.set(key, bin)
    }
  }
  const best = [...bins.values()].sort((a, b) => b.count - a.count)[0]
  return best && best.count >= Math.max(4, area.height)
    ? best.rgb.map(value => value / best.count) as unknown as Color : null
}

/** Stop at the first panel boundary, even when the scene beyond it is also blue/green. */
export function mapPanelRight(image: ImageData, area: PixelRect): number {
  const background = backgroundColor(image, area)
  const end = Math.min(image.width, area.x + area.width)
  if (!background) return area.x
  let last = area.x, misses = 0
  const gap = Math.max(3, Math.round(area.height / 4))
  const endY = Math.min(image.height, area.y + area.height), required = Math.ceil(area.height * .28)
  const [red, green, blue] = background
  for (let x = area.x; x < end; x++) {
    let matching = 0
    for (let y = area.y; y < endY && matching < required; y++) {
      const i = (y * image.width + x) * 4
      if (Math.abs(image.data[i]! - red) < 24 && Math.abs(image.data[i + 1]! - green) < 24
        && Math.abs(image.data[i + 2]! - blue) < 24) matching++
    }
    if (matching >= required) { last = x; misses = 0 }
    else if (++misses >= gap && x - area.x > area.height) return last + 1
  }
  return end
}

export function cropMapPixels(image: ImageData, rect: PixelRect): ImageData {
  const data = new Uint8ClampedArray(rect.width * rect.height * 4)
  for (let y = 0; y < rect.height; y++) data.set(image.data.subarray(
    ((rect.y + y) * image.width + rect.x) * 4,
    ((rect.y + y) * image.width + rect.x + rect.width) * 4), y * rect.width * 4)
  return new ImageData(data, rect.width, rect.height)
}

/** Retain the entire text line; only remove a proven panel border/scene to its right. */
export function mapLinePixels(image: ImageData): ImageData {
  const width = mapPanelRight(image, { x: 0, y: 0, width: image.width, height: image.height })
  return width < image.width && width >= image.height ? cropMapPixels(image, { x: 0, y: 0, width, height: image.height }) : image
}

export function mapInkBounds(image: ImageData): PixelRect | null {
  const color = backgroundColor(image, { x: 0, y: 0, width: image.width, height: image.height })
  if (!color) return null
  const background = color[0] * .299 + color[1] * .587 + color[2] * .114
  let left = image.width, top = image.height, right = -1, bottom = -1
  for (let y = 0; y < image.height; y++) {
    const columns: number[] = []
    for (let x = 0; x < image.width; x++) {
      const i = (y * image.width + x) * 4
      const r = image.data[i]!, g = image.data[i + 1]!, b = image.data[i + 2]!
      if (r * .299 + g * .587 + b * .114 > background + 24 && Math.max(r, g, b) - Math.min(r, g, b) < 45) columns.push(x)
    }
    // A horizontal minimap border is not part of the glyphs.
    if (!columns.length || columns.length > image.width * .8) continue
    left = Math.min(left, columns[0]!); right = Math.max(right, columns.at(-1)!)
    top = Math.min(top, y); bottom = y
  }
  return right >= left && bottom - top >= 4 ? { x: left, y: top, width: right - left + 1, height: bottom - top + 1 } : null
}

export const MAP_IMAGE_ENHANCEMENTS: readonly OcrTextImageEnhancement[] = [
  { scale: 12, padding: 2, contrast: 1.1, sharpen: 0, interpolation: 'smooth' },
  { scale: 12, padding: 3, contrast: 1.16, sharpen: .15, interpolation: 'smooth' },
]

export function prepareMapLine(source: PreparedOcrImage, enhancement?: OcrTextImageEnhancement, index = 0): PreparedOcrImage {
  const panel = mapLinePixels(source.source)
  const bounds = mapInkBounds(panel)
  // Canonical margins make a one-pixel calibration nudge independent of OCR
  // resampling phase. All bright glyphs, including a missed numeral, participate.
  const left = Math.max(0, (bounds?.x ?? 2) - 2)
  const right = bounds ? Math.min(panel.width, bounds.x + bounds.width + Math.max(3, Math.ceil(bounds.height * .3))) : panel.width
  const image = cropMapPixels(panel, { x: left, y: 0, width: right - left, height: panel.height })
  const height = index % 2 ? 64 : 48
  const scale = Math.max(1, Math.min(12, height / (bounds?.height ?? image.height)))
  const cropped = { ...source, source: image,
    sourceRect: { ...source.sourceRect,
      x: source.sourceRect.x + source.sourceRect.width * left / source.source.width,
      width: source.sourceRect.width * image.width / source.source.width } }
  return enhancePreparedOcrImage(cropped, { ...(enhancement ?? MAP_IMAGE_ENHANCEMENTS[index % 2]!),
    scale, targetContentHeight: undefined })
}

/** Binary text comparison makes a one-stroke suffix change visible without scene noise. */
export function mapLineChanged(previous: ImageData | null, current: ImageData): boolean {
  if (!previous || previous.width !== current.width || previous.height !== current.height) return true
  const bounds = mapInkBounds(current), oldBounds = mapInkBounds(previous)
  if (!bounds || !oldBounds) return bounds !== oldBounds
  let changed = 0
  for (let i = 0; i < current.data.length; i += 4) {
    const white = (pixels: Uint8ClampedArray) => pixels[i]! > 200 && pixels[i + 1]! > 200 && pixels[i + 2]! > 200
    if (white(current.data) !== white(previous.data)) changed++
  }
  return changed >= Math.max(3, Math.min(bounds.height, oldBounds.height) * .4)
}
